import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { App } from './App.tsx';
import { ToastProvider } from './components/Toast.tsx';
import './i18n/index.ts';
import './styles.css';

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
/** Invite, email-confirm and reset links carry secret tokens. */
const redact = (url: string) =>
  url
    .replace(/\/(invite|confirm-email)\/[^/?#]+/g, '/$1/REDACTED')
    .replace(/token=[^&#]+/g, 'token=REDACTED');
if (dsn) {
  // No personal data: no user info, no request bodies, no breadcrumbs with input values.
  Sentry.init({
    dsn,
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
    },
    beforeBreadcrumb: (b) => {
      if (b.category === 'ui.input') return null;
      if (b.data?.to) b.data.to = redact(String(b.data.to));
      if (b.data?.from) b.data.from = redact(String(b.data.from));
      if (b.data?.url) b.data.url = redact(String(b.data.url));
      return b;
    },
    beforeSend: (e) => {
      if (e.request?.url) e.request.url = redact(e.request.url);
      return e;
    },
  });
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: true } },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ToastProvider>
          <App />
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => void navigator.serviceWorker.register('/sw.js'));
}
